import PropTypes from "prop-types";
import React from "react";

import FormButton from "../../../components/form/FormButton";
import FormCheckbox from "../../../components/form/FormCheckbox";
import FormColorPicker from "../../../components/form/FormColorPicker";
import FormIconPicker from "../../../components/form/FormIconPicker";
import FormTextField from "../../../components/form/FormTextField";
import ModalFormDropdown from "../../../components/form/ModalFormDropdown";
import ModalCheckboxGrid from "../../../components/modal/ModalCheckboxGrid";
import ModalField from "../../../components/modal/ModalField";
import ModalGroup from "../../../components/modal/ModalGroup";
import ModalRow from "../../../components/modal/ModalRow";
import colorOptions from "../../../constants/colorOptions";
import lineStyles from "../../../constants/lineStyles";
import { REFERENCE_RELATES_TO } from "../../../constants/references";
import { relationshipClassToSvg, relationshipSvgToClass } from "../../../constants/relationshipIcons";
import { PropTypeShapes } from "../../../types";
import preprocessorQuery from "../../../utils/preprocessorQuery";
import userInputValidators from "../../../utils/userInputValidators";

const lineStyleOptions = [
  { value: lineStyles.THICK, label: "Thick" },
  { value: lineStyles.SOLID, label: "Solid" },
  { value: lineStyles.DASHED, label: "Dashed" },
  { value: lineStyles.DOTTED, label: "Dotted" },
  { value: lineStyles.DOTDASH, label: "Dot-dash" }
];

const iconNames = ["circle", "circle-f", "square", "square-f"];

const colorNames = ["iconMono"];

const iconOptions = () => {
  let options = [];
  iconNames.forEach(i => {
    colorNames.forEach(c => {
      options.push({ color: c, icon: i });
    });
  });
  return options;
};

const renderTopGroup = (
  r,
  onLabelChange,
  onColorChange,
  onLineStyleChange,
  onIconChange,
  onEnabledChange,
  onShowInInspectorChange
) => {
  return (
    <ModalGroup>
      <ModalRow>
        <ModalField label="Label" flex={4}>
          <FormTextField
            value={r.label}
            onChange={label => onLabelChange(r.id, label)}
            validator={userInputValidators.noEmpty}
          />
        </ModalField>
        <ModalField label="Color" flex={1}>
          <FormColorPicker
            value={r.color}
            options={colorOptions}
            id="settingsColorTabColorPicker"
            onChange={color => onColorChange(r.id, color)}
            title="Color"
            backText="Settings"
          />
        </ModalField>
        <ModalField label="Icon" flex={1}>
          <FormIconPicker
            value={{ icon: relationshipClassToSvg[r.icon], color: "iconMono" }}
            options={iconOptions()}
            id="settingsReferencesTabIconPicker"
            onChange={opt => onIconChange(r.id, relationshipSvgToClass[opt.icon])}
            title="Icon"
            backText="Settings"
          />
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField label="Mindmap Line" flex={1}>
          <ModalFormDropdown
            options={lineStyleOptions}
            value={r.lineStyle}
            onChange={lineStyle => onLineStyleChange(r.id, lineStyle)}
          />
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField>
          <FormCheckbox
            checkboxState={r.showInInspector}
            label={"Always show in inspector"}
            onChange={val => onShowInInspectorChange(r.id, val)}
            helpTooltip="The Inspector's Relationship tab will always expose this relationship property for relevant item types, even when there are no assigned values"
          />
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField>
          <FormCheckbox
            checkboxState={r.enabled}
            label={"Enabled"}
            onChange={val => onEnabledChange(r.id, val)}
            disabled={r.id === REFERENCE_RELATES_TO}
          />
        </ModalField>
      </ModalRow>
    </ModalGroup>
  );
};

const renderDirectionGroup = (r, onBidirectionalChange, onShowInverseChange, onInverseLabelChange) => {
  // TODO: Add icon selection
  return (
    <ModalGroup>
      <ModalRow>
        <ModalField flex={1} label="Inverse Relationship" />
      </ModalRow>
      <ModalRow label="Inverse Relationship">
        <ModalField flex={1}>
          <FormCheckbox
            label="Relationship Type is bi-directional"
            checkboxState={r.bidirectional}
            onChange={newState => onBidirectionalChange(r.id, newState)}
            disabled={r.id === REFERENCE_RELATES_TO}
            helpTooltip="If Item A has this relationship to Item B, then Item B has the same relationship to Item A. The relationship will be shown in the Inspector for both items in the relationship."
          />
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField flex={1}>
          <FormCheckbox
            disabled={r.bidirectional || r.id === REFERENCE_RELATES_TO}
            label="Show inverse in Inspector"
            checkboxState={r.showInverse}
            onChange={newState => onShowInverseChange(r.id, newState)}
            helpTooltip="If Item A has this relationship to Item B, then Item B has the specified inverse relationship to Item A, e.g. Instigator and Instigated By. The relationship will be shown in the Inspector for both items in the relationship, but with different property labels."
          />
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField flex={1} label="Inverse Label">
          <FormTextField
            disabled={r.bidirectional || !r.showInverse}
            value={r.inverseLabel}
            onChange={label => onInverseLabelChange(r.id, label)}
            validator={userInputValidators.noEmpty}
          />
        </ModalField>
      </ModalRow>
    </ModalGroup>
  );
};

const renderTypesList = (r, types, onAllowsTypeChange) => {
  const checkboxes = types.allIds
    .map(tId => {
      const t = types.byId.get(tId);
      const isChecked = r.types.indexOf(tId) >= 0;
      return (
        <FormCheckbox
          key={t.id}
          label={t.label}
          checkboxState={isChecked}
          onChange={newState => onAllowsTypeChange(r.id, t.id, newState)}
          disabled={r.id === REFERENCE_RELATES_TO}
        />
      );
    })
    .toArray();

  return (
    <ModalGroup key="allowTypes">
      <ModalRow>
        <ModalField
          flex={1}
          label={`Allowed item types for this relationship`}
          helpTooltip={`The chosen types will be acceptable values for this relationship field (e.g. a "Location" would be an acceptable value for a "Birthplace" relationship)`}
        >
          <ModalCheckboxGrid columns={2}>{checkboxes}</ModalCheckboxGrid>
        </ModalField>
      </ModalRow>
    </ModalGroup>
  );
};

const renderIncludeInTypesList = (r, types, onIncludeInTypeChange) => {
  const checkboxes = types.allIds
    .map(tId => {
      const t = types.byId.get(tId);
      const isChecked = t.references.indexOf(r.id) >= 0;
      return (
        <FormCheckbox
          key={t.id}
          label={t.label}
          checkboxState={isChecked}
          onChange={newState => onIncludeInTypeChange(t.id, r.id, newState)}
          disabled={r.id === REFERENCE_RELATES_TO}
        />
      );
    })
    .toArray();

  return (
    <ModalGroup key="includeInTypes">
      <ModalRow>
        <ModalField
          flex={1}
          label={`Item Types with a field for this relationship`}
          helpTooltip={`Items of the chosen types will have fields in the Inspector and Spreadsheet for this relationship (e.g. a "Person" would have a relationship field called "Birthplace"). This mirrors the setting in the Types tab.`}
        >
          <ModalCheckboxGrid columns={2}>{checkboxes}</ModalCheckboxGrid>
        </ModalField>
      </ModalRow>
    </ModalGroup>
  );
};

const SettingsReferenceDetailView = props => {
  const isIos = preprocessorQuery.isIos();
  const r = props.selectedItem ? props.references.byId.get(props.selectedItem) : null;
  if (!r) {
    return <div />;
  }

  return (
    <div>
      {renderTopGroup(
        r,
        props.onLabelChange,
        props.onColorChange,
        props.onLineStyleChange,
        props.onIconChange,
        props.onEnabledChange,
        props.onShowInInspectorChange
      )}
      {renderDirectionGroup(r, props.onBidirectionalChange, props.onShowInverseChange, props.onInverseLabelChange)}
      {renderIncludeInTypesList(r, props.types, props.onIncludeInTypeChange)}
      {renderTypesList(r, props.types, props.onAllowsTypeChange)}
      {isIos && props.canDelete ? (
        <ModalGroup>
          <ModalRow>
            <div className="settingsDetailDeleteButton">
              <FormButton destructive={true} label="Delete" onClick={props.onDeleteClick} />
            </div>
          </ModalRow>
        </ModalGroup>
      ) : null}
    </div>
  );
};

SettingsReferenceDetailView.propTypes = {
  references: PropTypeShapes.denormalisedReferenceDefinitionsIdList.isRequired,
  types: PropTypeShapes.denormalisedTypeDefinitionsIdList.isRequired,
  selectedItem: PropTypes.string,
  onIconChange: PropTypes.func.isRequired,
  onColorChange: PropTypes.func.isRequired,
  onLineStyleChange: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func.isRequired,
  onBidirectionalChange: PropTypes.func.isRequired,
  onShowInverseChange: PropTypes.func.isRequired,
  onInverseLabelChange: PropTypes.func.isRequired,
  onAllowsTypeChange: PropTypes.func.isRequired,
  onIncludeInTypeChange: PropTypes.func.isRequired,
  canDelete: PropTypes.bool,
  onDeleteClick: PropTypes.func,
  onEnabledChange: PropTypes.func.isRequired,
  onShowInInspectorChange: PropTypes.func.isRequired
};

export default SettingsReferenceDetailView;
